import React, { useState } from 'react';
import { Button, InputNumber, Select, message, Row, Col, Space, Checkbox, Table } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { usePhaserConfig } from '../contexts/PhaserConfigContext';
import { TObstacle } from '../../../models/objects/objects';
import { ObjectConfig } from '../types/config';
import { getImagePath } from '../../../utils/format/valueFormat';

interface ObjectConfiguratorProps {
    obstacles: TObstacle[]; // Liste des obstacles disponibles depuis la BDD
}

const { Option } = Select;

const ObjectConfigurator: React.FC<ObjectConfiguratorProps> = ({ obstacles }) => {
    const { objects, addObject, updateObject, removeObject } = usePhaserConfig();

    const [selectedObstacleId, setSelectedObstacleId] = useState<number | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [currentObject, setCurrentObject] = useState<ObjectConfig | null>(null);
    const [isModified, setIsModified] = useState<boolean>(false);

    // Gestionnaire de sauvegarde
    const handleSaveObject = () => {
        if (!selectedObstacleId) {
            message.warning("Veuillez sélectionner un obstacle.");
            return;
        }

        const dbObstacle = obstacles.find((o) => o.id === selectedObstacleId);
        if (!dbObstacle) {
            message.error("Obstacle introuvable dans la liste BDD.");
            return;
        }
        const newObject: ObjectConfig = {
            id: currentObject?.id || crypto.randomUUID(),
            idPhaser: `${dbObstacle.name.replaceAll(" ", "_")}`,
            name: dbObstacle.name,
            x: currentObject?.x ?? 200,
            y: currentObject?.y ?? 200,
            width: currentObject?.width || 50,
            height: currentObject?.height || 50,
            immovable: currentObject?.immovable ?? true,
            pickable: currentObject?.pickable ?? false,
            pickOrder: currentObject?.pickOrder || (objects.length + 1),
            pushable: currentObject?.pushable ?? false,
            assets: {
                texture: dbObstacle.img || ''
            }
        };

        if (currentObject && currentObject.id) {
            updateObject(currentObject.id, newObject);
        } else {
            addObject(newObject);
        }

        setIsEditing(false);
        setCurrentObject(null);
        setSelectedObstacleId(null);
        setIsModified(false);
    };

    // Gestion des changements de champs
    const handleFieldChange = (field: keyof ObjectConfig, value: any) => {
        setCurrentObject(prev => ({ ...prev!, [field]: value }));
        setIsModified(true);
    };

    // ==============================================
    // 3) AFFICHAGE DE LA GRILLE DES OBJETS
    // ==============================================
    const renderObjectTable = () => {
        const columns = [
            /* Aperçu */
            {
                dataIndex: 'preview',
                width: 110,
                render: (_: any, obj: ObjectConfig) => (
                    <div
                        style={{
                            width: 100,
                            height: 100,
                            background: '#f6f6f6',
                            borderRadius: 8,
                            overflow: 'hidden',
                        }}
                    >
                        <img
                            src={getImagePath(obj.assets.texture)}
                            alt={obj.name}
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                    </div>
                ),
            },
            /* Nom + ID */
            {
                dataIndex: 'name',
                render: (_: any, obj: ObjectConfig) => (
                    <div>
                        <strong>{obj.name}</strong>
                        <div style={{ fontSize: 12, color: '#888' }}>ID : {obj.id}</div>
                    </div>
                ),
            },
            /* Actions */
            {
                key: 'actions',
                align: 'right' as const,
                width: 90,
                render: (_: any, obj: ObjectConfig) => (
                    <Space>
                        <Button
                            icon={<EditOutlined />}
                            size="small"
                            onClick={() => {
                                setCurrentObject(obj);
                                const obstacle = obstacles.find(o => o.name === obj.name);
                                if (obstacle) setSelectedObstacleId(obstacle.id);
                                setIsEditing(true);
                            }}
                        />
                        <Button
                            icon={<DeleteOutlined />}
                            danger
                            size="small"
                            onClick={() => removeObject(obj.id)}
                        />
                    </Space>
                ),
            },
        ];

        return (
            <>
                {/* Bouton “Ajouter” aligné à droite */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setCurrentObject(null);
                            setSelectedObstacleId(null);
                            setIsEditing(true);
                        }}
                    >
                        Ajouter
                    </Button>
                </div>

                <Table<ObjectConfig>
                    columns={columns}
                    dataSource={objects}
                    rowKey="id"
                    pagination={false}
                    showHeader={false}   /* 🌱  Pas d’en‑tête */
                    size="small"         /* 🌱  Compacte */
                    bordered={false}     /* 🌱  Sans bordure */
                />
            </>
        );
    };


    // Formulaire de configuration
    const renderConfigForm = () => {

        return (
            <div>
                <Select
                    placeholder="Choisir un obstacle"
                    style={{ width: '100%', marginBottom: '16px' }}
                    value={selectedObstacleId}
                    onChange={value => setSelectedObstacleId(value)}
                    optionLabelProp="label"
                >
                    {obstacles.map(obs => (
                        <Option key={obs.id} value={obs.id} label={obs.name}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <img
                                    src={getImagePath(obs.img)}
                                    alt={obs.name}
                                    style={{ width: '30px', height: '30px', marginRight: '8px' }}
                                />
                                <span>{obs.name}</span>
                            </div>
                        </Option>
                    ))}
                </Select>

                <Row gutter={16} style={{ marginBottom: '16px' }}>
                    <Col span={12}>
                        <InputNumber
                            placeholder="Position X"
                            value={currentObject?.x}
                            onChange={value => handleFieldChange('x', value || 0)}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col span={12}>
                        <InputNumber
                            placeholder="Position Y"
                            value={currentObject?.y}
                            onChange={value => handleFieldChange('y', value || 0)}
                            style={{ width: '100%' }}
                        />
                    </Col>
                </Row>

                <Row gutter={16} style={{ marginBottom: '16px' }}>
                    <Col span={12}>
                        <InputNumber
                            placeholder="Largeur"
                            value={currentObject?.width}
                            onChange={value => handleFieldChange('width', value || 50)}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col span={12}>
                        <InputNumber
                            placeholder="Hauteur"
                            value={currentObject?.height}
                            onChange={value => handleFieldChange('height', value || 50)}
                            style={{ width: '100%' }}
                        />
                    </Col>
                </Row>

                <Checkbox
                    checked={currentObject?.immovable ?? true}
                    onChange={e => handleFieldChange('immovable', e.target.checked)}
                    style={{ marginBottom: '8px' }}
                >
                    Immovable
                </Checkbox>

                <Checkbox
                    checked={currentObject?.pickable ?? false}
                    onChange={e => handleFieldChange('pickable', e.target.checked)}
                    style={{ marginBottom: '8px', marginLeft: '8px' }}
                >
                    Pickable
                </Checkbox>

                <Checkbox
                    checked={currentObject?.pushable ?? false}
                    onChange={e => handleFieldChange('pushable', e.target.checked)}
                    style={{ marginBottom: '8px', marginLeft: '8px' }}
                >
                    Pushable
                </Checkbox>

                {currentObject?.pickable && (
                    <InputNumber
                        placeholder="Pick Order"
                        value={currentObject?.pickOrder}
                        onChange={value => handleFieldChange('pickOrder', value || 1)}
                        style={{ width: '100%', marginBottom: '16px' }}
                    />
                )}

                <div style={{ textAlign: 'center' }}>
                    <Button type="primary" onClick={handleSaveObject}>
                        {isModified || currentObject ? "Enregistrer" : "Ajouter"}
                    </Button>
                    <Button style={{ marginLeft: '8px' }} onClick={() => setIsEditing(false)}>
                        Annuler
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <div>
            {isEditing ? renderConfigForm() : renderObjectTable()}
        </div>
    );
};

export default ObjectConfigurator;
